import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import PrinterService from '../../services/printer';

const TicketPreviewModal = ({ storeId, idSales, open, onClose, onPrint }) => {
  const [html, setHtml] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const loadPreview = useCallback(async () => {
    if (!storeId || !idSales?.length) return;
    setLoading(true);
    setError('');
    try {
      const data = await PrinterService.getTicketPreview(storeId, idSales);
      setHtml(data);
    } catch (err) {
      const msg = err?.response?.data?.error || 'No se pudo cargar la vista previa.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [storeId, idSales]);

  useEffect(() => {
    if (open) {
      loadPreview();
    } else {
      setHtml('');
      setPdfUrl('');
      setError('');
    }
  }, [open, loadPreview]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handlePrint = async () => {
    setPrinting(true);

    try {
      // Deduplicar idSales para evitar imprimir duplicados
      const uniqueIds = Array.from(
        new Set(Array.isArray(idSales) ? idSales : [idSales])
      );
      
      if (!uniqueIds.length) {
        setSnack({ open: true, severity: 'warning', message: 'No hay ventas para imprimir.' });
        setPrinting(false);
        return;
      }

      const activePrinter = await PrinterService.getActivePrinter();
      if (activePrinter?.printerType === 'pdf') {
        const idSalesCsv = uniqueIds.join(',');
        await PrinterService.downloadTicketPreviewAsPDF(storeId, idSalesCsv);
        setSnack({ open: true, severity: 'success', message: 'PDF descargado correctamente.' });
        onPrint?.();
        return;
      }

      const res = await PrinterService.printSales(storeId, uniqueIds);
      if (res && res.pdf && res.blob) {
        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
        }

        const url = URL.createObjectURL(res.blob);
        setPdfUrl(url);
        setHtml('');

        const link = document.createElement('a');
        link.href = url;
        link.download = `ticket-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setSnack({ open: true, severity: 'success', message: 'PDF descargado correctamente.' });
        onPrint?.();
        return;
      }

      setSnack({ open: true, severity: 'success', message: 'Ticket impreso correctamente.' });
      onPrint?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'No se pudo imprimir el ticket.';
      if (msg.toLowerCase().includes('no hay impresora') || msg.toLowerCase().includes('no active')) {
        setSnack({ open: true, severity: 'warning', message: 'No hay impresora activa configurada.' });
      } else {
        setSnack({ open: true, severity: 'error', message: msg });
      }
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: '#fcf8f2' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Vista previa del ticket
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={3}>
              <Typography color="error" variant="body2" textAlign="center">
                {error}
              </Typography>
              <Button startIcon={<RefreshIcon />} onClick={loadPreview} size="small" variant="outlined">
                Reintentar
              </Button>
            </Box>
          )}

          {!loading && !error && pdfUrl && (
            <Box display="flex" justifyContent="center">
              <iframe
                title="ticket-preview-pdf"
                src={pdfUrl}
                style={{
                  width: 320,
                  height: 500,
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  background: '#fff',
                  display: 'block',
                }}
              />
            </Box>
          )}

          {!loading && !error && !pdfUrl && html && (
            <Box display="flex" justifyContent="center">
              <iframe
                title="ticket-preview"
                srcDoc={html}
                style={{
                  width: 320,
                  height: 500,
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  background: '#fff',
                  display: 'block',
                }}
                sandbox="allow-same-origin"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" size="small">
            Cerrar
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={printing ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
            disabled={printing || loading || !!error || (!html && !pdfUrl)}
            size="small"
          >
            {printing ? 'Imprimiendo...' : 'Imprimir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TicketPreviewModal;
